// src/components/LibraryManager.jsx 
import React, { useState, useEffect } from "react";
import {
  collection,
  addDoc,
  updateDoc,
  doc,
  deleteDoc,
  onSnapshot,
} from "firebase/firestore";
import { db } from "../Firebase/firebaseConfig";
import { uploadFile, deleteFile } from "../Firebase/firebaseStorage";
import {
  Container,
  Row,
  Col,
  Form,
  Button,
  Table,
  Spinner,
  Modal,
  Alert,
  Card,
} from "react-bootstrap";
import "bootstrap/dist/css/bootstrap.min.css";

// Move booksCollectionRef outside the component to stabilize its reference
const booksCollectionRef = collection(db, "books");

const LibraryManager = () => {
  const [books, setBooks] = useState([]);
  const [formData, setFormData] = useState({
    name: { en: "", ar: "" },
    author: { en: "", ar: "" },
    translator: { en: "", ar: "" },
    originalLanguage: { en: "", ar: "" },
    year: "", // Renamed from publicationYear to year
    description: { en: "", ar: "" },
    coverImage: null,
  });
  const [editBook, setEditBook] = useState(null);
  const [selectedBook, setSelectedBook] = useState(null);
  const [loading, setLoading] = useState(false);
  const [operationLoading, setOperationLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [bookToDelete, setBookToDelete] = useState(null);

  // Fetch books in real-time
  useEffect(() => {
    setLoading(true);
    const unsubscribe = onSnapshot(
      booksCollectionRef,
      (snapshot) => {
        const booksData = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setBooks(booksData);
        setLoading(false);
      },
      (err) => {
        console.error("Error fetching books:", err);
        setError("Failed to fetch books.");
        setLoading(false);
      }
    );

    // Cleanup subscription on unmount
    return () => unsubscribe();
  }, []); // Removed 'booksCollectionRef' from dependencies

  // Handle input changes
  const handleChange = (e) => {
    const { name, value, files, dataset } = e.target;
    const lang = dataset.lang;

    if (name === "coverImage") {
      setFormData((prev) => ({ ...prev, coverImage: files[0] }));
    } else if (lang) {
      setFormData((prev) => ({
        ...prev,
        [name]: { ...prev[name], [lang]: value },
      }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      name: { en: "", ar: "" },
      author: { en: "", ar: "" },
      translator: { en: "", ar: "" },
      originalLanguage: { en: "", ar: "" },
      year: "", // Reset year
      description: { en: "", ar: "" },
      coverImage: null,
    });
    setEditBook(null);
    setError(null);
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setOperationLoading(true);

    try {
      let coverImageUrl = editBook?.coverImageUrl || null;
      let coverImagePath = editBook?.coverImagePath || null;

      if (formData.coverImage) {
        // Upload new cover image
        const uploadResult = await uploadFile(formData.coverImage, "books");
        coverImageUrl = uploadResult.downloadURL;
        coverImagePath = uploadResult.filePath;

        // If editing and there's an old cover image, delete it
        if (editBook?.coverImagePath) {
          await deleteFile(editBook.coverImagePath);
        }
      }

      const bookData = {
        name: formData.name,
        author: formData.author,
        translator: formData.translator,
        originalLanguage: formData.originalLanguage,
        year: formData.year, // Store year as string
        description: formData.description,
        coverImageUrl,
        coverImagePath, // Store the path for deletion
      };

      if (editBook) {
        const bookDocRef = doc(db, "books", editBook.id);
        await updateDoc(bookDocRef, bookData);
      } else {
        await addDoc(booksCollectionRef, bookData);
      }

      resetForm();
    } catch (err) {
      console.error("Error submitting form:", err);
      setError("Failed to submit the form.");
    } finally {
      setOperationLoading(false);
    }
  };

  // Confirm delete
  const confirmDelete = (book) => {
    setBookToDelete(book);
    setShowDeleteModal(true);
  };

  // Handle delete
  const handleDelete = async () => {
    if (!bookToDelete) {
      setError("No book selected for deletion.");
      setShowDeleteModal(false);
      return;
    }

    setOperationLoading(true);
    try {
      console.log("Deleting file:", bookToDelete.coverImagePath); // Debugging line

      if (bookToDelete.coverImagePath) {
        await deleteFile(bookToDelete.coverImagePath);
      }
      const bookDocRef = doc(db, "books", bookToDelete.id);
      await deleteDoc(bookDocRef);

      setShowDeleteModal(false);
    } catch (err) {
      console.error("Error deleting book:", err);
      setError("Failed to delete the book.");
    } finally {
      setOperationLoading(false);
    }
  };

  // Handle edit
  const handleEdit = (book) => {
    setEditBook(book);
    setFormData({
      name: { en: book.name.en || "", ar: book.name.ar || "" },
      author: { en: book.author.en || "", ar: book.author.ar || "" },
      translator: { en: book.translator.en || "", ar: book.translator.ar || "" },
      originalLanguage: {
        en: book.originalLanguage.en || "",
        ar: book.originalLanguage.ar || "",
      },
      year: book.year || "", // Populate year
      description: { en: book.description.en || "", ar: book.description.ar || "" },
      coverImage: null,
    });
    setError(null);
  };

  // Handle view details
  const handleViewDetails = (book) => {
    setSelectedBook(book);
    setShowDetailsModal(true);
  };

  return (
    <Container fluid className="p-4">
      {/* Form Section */}
      <Row>
        <Col lg={8} className="mx-auto">
          <Card className="shadow-sm">
            <Card.Body>
              <h2 className="text-center">{editBook ? "Edit Book" : "Add New Book"}</h2>
              {error && (
                <Alert variant="danger" onClose={() => setError(null)} dismissible>
                  {error}
                </Alert>
              )}
              <Form onSubmit={handleSubmit}>
                {/* Name */}
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Book Name (EN)</Form.Label>
                      <Form.Control
                        type="text"
                        name="name"
                        data-lang="en"
                        value={formData.name.en}
                        onChange={handleChange}
                        required
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Book Name (AR)</Form.Label>
                      <Form.Control
                        type="text"
                        name="name"
                        data-lang="ar"
                        value={formData.name.ar}
                        onChange={handleChange}
                        required
                      />
                    </Form.Group>
                  </Col>
                </Row>

                {/* Author */}
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Author (EN)</Form.Label>
                      <Form.Control
                        type="text"
                        name="author"
                        data-lang="en"
                        value={formData.author.en}
                        onChange={handleChange}
                        required
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Author (AR)</Form.Label>
                      <Form.Control
                        type="text"
                        name="author"
                        data-lang="ar"
                        value={formData.author.ar}
                        onChange={handleChange}
                        required
                      />
                    </Form.Group>
                  </Col>
                </Row>

                {/* Translator */}
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Translator (EN)</Form.Label>
                      <Form.Control
                        type="text"
                        name="translator"
                        data-lang="en"
                        value={formData.translator.en}
                        onChange={handleChange}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Translator (AR)</Form.Label>
                      <Form.Control
                        type="text"
                        name="translator"
                        data-lang="ar"
                        value={formData.translator.ar}
                        onChange={handleChange}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                {/* Original Language */}
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Original Language (EN)</Form.Label>
                      <Form.Control
                        type="text"
                        name="originalLanguage"
                        data-lang="en"
                        value={formData.originalLanguage.en}
                        onChange={handleChange}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Original Language (AR)</Form.Label>
                      <Form.Control
                        type="text"
                        name="originalLanguage"
                        data-lang="ar"
                        value={formData.originalLanguage.ar}
                        onChange={handleChange}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                {/* Year */}
                <Form.Group className="mb-3">
                  <Form.Label>Year</Form.Label> {/* Updated label */}
                  <Form.Control
                    type="text" // Changed from "number" to "text"
                    name="year" // Renamed from publicationYear to year
                    value={formData.year}
                    onChange={handleChange}
                    required
                    placeholder="e.g., 2023 or 2023/2024"
                  />
                </Form.Group>

                {/* Description */}
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Description (EN)</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        name="description"
                        data-lang="en"
                        value={formData.description.en}
                        onChange={handleChange}
                        required
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Description (AR)</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        name="description"
                        data-lang="ar"
                        value={formData.description.ar}
                        onChange={handleChange}
                        required
                      />
                    </Form.Group>
                  </Col>
                </Row>

                {/* Cover Image */}
                <Form.Group className="mb-3">
                  <Form.Label>Cover Image</Form.Label>
                  <Form.Control
                    type="file"
                    name="coverImage"
                    onChange={handleChange}
                    accept="image/*"
                  />
                  {editBook && editBook.coverImageUrl && (
                    <div className="mt-2">
                      <img
                        src={editBook.coverImageUrl}
                        alt="Current Cover"
                        style={{ width: "100px", height: "150px", objectFit: "cover" }}
                      />
                      <p className="mt-1">Current Cover Image</p>
                    </div>
                  )}
                </Form.Group>

                {/* Submit Button */}
                <Button
                  type="submit"
                  variant="primary"
                  className="mt-3 w-100"
                  disabled={operationLoading}
                >
                  {operationLoading ? (
                    <>
                      <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        role="status"
                        aria-hidden="true"
                      />{" "}
                      Processing...
                    </>
                  ) : editBook ? (
                    "Update Book"
                  ) : (
                    "Add Book"
                  )}
                </Button>
              </Form>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Table Section */}
      <Row className="mt-4">
        <Col>
          <Card className="shadow-sm">
            <Card.Body>
              <h2>Books List</h2>
              {loading ? (
                <div className="text-center my-4">
                  <Spinner animation="border" />
                </div>
              ) : books.length === 0 ? (
                <p>No books available.</p>
              ) : (
                <Table striped bordered hover responsive>
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Cover</th>
                      <th>Name (EN)</th>
                      <th>Name (AR)</th>
                      <th>Author (EN)</th>
                      <th>Author (AR)</th>
                      <th>Year</th> {/* Updated header */}
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {books.map((book, index) => (
                      <tr key={book.id}>
                        <td>{index + 1}</td>
                        <td>
                          <img
                            src={book.coverImageUrl || "https://via.placeholder.com/60x90?text=No+Image"}
                            alt="Book Cover"
                            style={{ width: "60px", height: "90px", objectFit: "cover" }}
                          />
                        </td>
                        <td>{book.name.en}</td>
                        <td>{book.name.ar}</td>
                        <td>{book.author.en}</td>
                        <td>{book.author.ar}</td>
                        <td>{book.year}</td> {/* Updated field */}
                        <td>
                          <Button
                            variant="info"
                            size="sm"
                            className="me-2 mb-1"
                            onClick={() => handleViewDetails(book)}
                          >
                            View
                          </Button>
                          <Button
                            variant="warning"
                            size="sm"
                            className="me-2 mb-1"
                            onClick={() => handleEdit(book)}
                          >
                            Edit
                          </Button>
                          <Button
                            variant="danger"
                            size="sm"
                            className="mb-1"
                            onClick={() => confirmDelete(book)}
                          >
                            Delete
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete <strong>{bookToDelete?.name?.en}</strong>?
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDelete} disabled={operationLoading}>
            {operationLoading ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                />{" "}
                Deleting...
              </>
            ) : (
              "Delete"
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Details Modal */}
      <Modal show={showDetailsModal} onHide={() => setShowDetailsModal(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>Book Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedBook && (
            <Row>
              <Col md={4} className="text-center">
                <img
                  src={selectedBook.coverImageUrl || "https://via.placeholder.com/150x225?text=No+Image"}
                  alt="Book Cover"
                  style={{ width: "150px", height: "225px", objectFit: "cover" }}
                />
              </Col>
              <Col md={8}>
                <p>
                  <strong>Title (EN):</strong> {selectedBook.name?.en || "N/A"}
                </p>
                <p>
                  <strong>Title (AR):</strong> {selectedBook.name?.ar || "N/A"}
                </p>
                <p>
                  <strong>Author (EN):</strong> {selectedBook.author?.en || "N/A"}
                </p>
                <p>
                  <strong>Author (AR):</strong> {selectedBook.author?.ar || "N/A"}
                </p>
                <p>
                  <strong>Translator (EN):</strong> {selectedBook.translator?.en || "N/A"}
                </p>
                <p>
                  <strong>Translator (AR):</strong> {selectedBook.translator?.ar || "N/A"}
                </p>
                <p>
                  <strong>Original Language (EN):</strong> {selectedBook.originalLanguage?.en || "N/A"}
                </p>
                <p>
                  <strong>Original Language (AR):</strong> {selectedBook.originalLanguage?.ar || "N/A"}
                </p>
                <p>
                  <strong>Description (EN):</strong> {selectedBook.description?.en || "N/A"}
                </p>
                <p>
                  <strong>Description (AR):</strong> {selectedBook.description?.ar || "N/A"}
                </p>
                <p>
                  <strong>Year:</strong> {selectedBook.year || "N/A"}
                </p>
              </Col>
            </Row>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDetailsModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default LibraryManager;
